/**
 * Одно место, где имя чемпиона превращается в идентификатор Data Dragon (KAN-921).
 *
 * До этого модуля таких мест было три, и каждое решало задачу по-своему:
 * список ручных исключений в `champion-image.ts`, нормализация в статистике
 * тирмейкера и карта «имя → id» на бэкенде. Они расходятся **молча и по-разному**:
 * Riot переименовывает чемпионов (Nunu стал «Nunu & Willump», у Aurelion Sol
 * появился пробел), и после такого переименования у одного не прилипает число
 * к чемпиону, у другого не грузится картинка, у третьего не матчится реплей.
 * Три симптома одной причины, которые чинят порознь.
 *
 * ## Как это работает
 *
 * Сначала **правило**, потом **данные**, и только в конце — ручной список.
 *
 * 1. **Нормализация** (`normalizeChampionKey`): оставляем латиницу и цифры в нижнем
 *    регистре. Этим закрывается вся пунктуация разом — пробелы, апострофы (включая
 *    фигурный U+2019, который LCU присылает вперемешку с обычным), точки, амперсанды.
 *    `Kai'Sa`, `Kai’Sa`, `KaiSa` и `kaisa` дают один ключ.
 * 2. **Каталог** (`registerChampionCatalog`): список чемпионов патча с id и английским
 *    именем. Он живой — приходит с CDN или из ручки, — поэтому переименования
 *    переживает сам, без правок в коде.
 * 3. **Ручной список** — только для случаев, где расхождение не в пунктуации, а в
 *    самом слове, и где каталога может не оказаться под рукой.
 *
 * ## Чего здесь нельзя делать
 *
 * **Нормализовать локализованное имя.** Кириллица не проходит фильтр «латиница и
 * цифры» и схлопывается в пустую строку — все чемпионы получили бы один ключ,
 * а сопоставление стало бы случайным. Сопоставляем только по id и английскому имени.
 *
 * **Дописывать в ручной список то, что решается правилом.** Каждая такая строка
 * устаревает молча: правило переживёт переименование, строка — нет.
 */

/**
 * Ключ для сравнения имён: только латиница и цифры, нижний регистр.
 *
 * Кириллица сюда не годится (см. комментарий к файлу) — на русском имени функция
 * вернёт пустую строку, и это НЕ ошибка вызова, а признак «сравнивать нечем».
 * Вызывающий обязан проверять результат на пустоту.
 */
export function normalizeChampionKey(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Случаи, где нормализация бессильна: id и отображаемое имя — разные СЛОВА,
 * а не разное написание одного слова.
 *
 * Держим их здесь, потому что каталог доступен не всегда (первый рендер, оффлайн,
 * серверный рендер). Как только каталог зарегистрирован, он перекрывает этот список:
 * если Riot переименует Вуконга ещё раз, поправить надо будет каталог, а не код.
 */
const IRREGULAR_NAMES: Readonly<Record<string, string>> = {
  wukong: "MonkeyKing",
  nunuwillump: "Nunu",
  renataglasc: "Renata",
};

/** Ключ → id, собирается из каталога чемпионов патча. */
const catalogIndex = new Map<string, string>();

/**
 * Запоминает каталог патча: по нему и разрешаются имена.
 *
 * Зовётся там, где каталог уже загружен (список чемпионов комнаты драфта). Повторный
 * вызов перезаписывает индекс — патч меняется, и старые записи не должны переживать
 * смену версии.
 */
export function registerChampionCatalog(
  champions: readonly { id: string; nameEn?: string | null }[],
): void {
  catalogIndex.clear();
  for (const champ of champions) {
    if (!champ.id) continue;
    const byId = normalizeChampionKey(champ.id);
    if (byId) catalogIndex.set(byId, champ.id);
    // Второй ключ — по английскому имени: он ловит как раз те случаи, где id и имя
    // разные слова («Wukong» → MonkeyKing), без ручного списка.
    const byName = champ.nameEn ? normalizeChampionKey(champ.nameEn) : "";
    if (byName) catalogIndex.set(byName, champ.id);
  }
}

/** Сколько чемпионов сейчас в индексе — для диагностики и тестов. */
export function championCatalogSize(): number {
  return catalogIndex.size;
}

/**
 * Имя из LCU, реплея или интерфейса → идентификатор Data Dragon.
 *
 * Возвращает исходную строку, если сопоставить не с чем: пустой каталог и имя вне
 * ручного списка. Это осознанно — лучше отдать имя как есть (URL картинки, возможно,
 * всё равно совпадёт) и увидеть 404, чем подставить чужого чемпиона.
 */
export function resolveChampionId(name: string): string {
  const raw = (name ?? "").trim();
  if (!raw) return raw;

  const key = normalizeChampionKey(raw);
  // Кириллица (и любое имя без латиницы) даёт пустой ключ — сравнивать нечем.
  if (!key) return raw;

  return catalogIndex.get(key) ?? IRREGULAR_NAMES[key] ?? raw;
}

/** Одинаковые ли это чемпионы, как бы ни было записано имя. */
export function isSameChampion(a: string, b: string): boolean {
  const left = normalizeChampionKey(a);
  const right = normalizeChampionKey(b);
  if (!left || !right) return false;
  return left === right || resolveChampionId(a) === resolveChampionId(b);
}
